// Main predictive search functionality with Boost AI integration
if (typeof PredictiveSearch === "undefined") {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedMap = new Map();
      this.boundHandleClickOutside = this.handleClickOutside.bind(this);
      this.boundClear = this.clear.bind(this);
      this.boundOnChange =
        FoxTheme?.utils?.debounce(this.onChange.bind(this), 300) ||
        this.onChange.bind(this);
      this.boundOnFocus = this.onFocus.bind(this);
      this.boundHandleProductTypeChange = this.handleProductTypeChange.bind(this);
      this.market = this.dataset.shopMarket || "AT"
      this.currency = this.dataset.shopCurrency || "EUR"

      this.states = {
        OPEN: "predictive-search-open",
        LOADING: "btn--loading",
        SEARCH_OPEN: "search-open",
      };
    }

    connectedCallback() {
      if (this.hasConnected) {
        return;
      }
      this.hasConnected = true;

      this.focusElement = this.input;
      if (this.resetButton) {
        this.resetButton?.addEventListener("click", this.boundClear);
      }
      if (this.input) {
        this.input.addEventListener("input", this.boundOnChange);
        this.input.addEventListener("focus", this.boundOnFocus);
      }
      this.searchContent = this.querySelector(".search__content");
      this.searchRecommendationEmpty =
        this.dataset.searchRecommendationEmpty === "true";
      this.header = document.querySelector("header");
      this.toggleNavigationButton = document.querySelector(
        ".toggle-navigation-button",
      );

      document.addEventListener("click", this.boundHandleClickOutside);
      if (this.searchProductTypes) {
        this.searchProductTypes.addEventListener(
          "change",
          this.boundHandleProductTypeChange,
        );
      }

      this.isHeaderSearch = this.closest("header") !== null;

      document.addEventListener("menu-drawer:open", () => {
        this.classList.remove("predictive-search-open");
        document.body.classList.remove("search-open");
      });
    }

    disconnectedCallback() {
      if (this.resetButton) {
        this.resetButton?.removeEventListener("click", this.boundClear);
      }
      if (this.input) {
        this.input.removeEventListener("input", this.boundOnChange);
        this.input.removeEventListener("focus", this.boundOnFocus);
      }
      document.removeEventListener("click", this.boundHandleClickOutside);
      if (this.searchProductTypes) {
        this.searchProductTypes.removeEventListener(
          "change",
          this.boundHandleProductTypeChange,
        );
      }
    }

    get input() {
      return this.querySelector('input[type="search"]');
    }
    get resetButton() {
      return this.querySelector('button[type="reset"]');
    }

    get searchProductTypes() {
      return this.querySelector("#SearchProductTypes");
    }

    onFocus(event) {
      document.documentElement.style.setProperty(
        "--viewport-height",
        `${window.innerHeight}px`,
      );
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(this.header.getBoundingClientRect().bottom)}px`,
      );
      if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
        document.body.classList.add("search-open");
        this.toggleNavButtonVisibility(false);
      }

      if (!this.searchRecommendationEmpty) {
        this.searchContent.classList.remove("hidden");
      }
      this.classList.add("predictive-search-open");
      if (this.getQuery().length === 0) {
        if (this.searchRecommendationEmpty) {
          this.searchContent.classList.add("hidden");
        }
        return;
      }
      const url = this.setupURL().toString();
      this.renderSection(url, event);
    }

    getQuery() {
      return this.input.value.trim();
    }

    clear(event = null) {
      event?.preventDefault();
      this.input.value = "";
      this.input.focus();
      this.removeAttribute("results");
      this.toggleSearchState(false);
    }

    handleProductTypeChange(evt) {
      const query = this.getQuery();
      if (query.length > 0) {
        const url = this.setupURL().toString();
        this.renderSection(url);
      }
    }

    setupURL() {
      const url = new URL(
        `${window.shopUrl}${FoxTheme.routes.predictive_search_url}`,
      );
      let searchTerm = this.getQuery();
      if (this.searchProductTypes && !!this.searchProductTypes.value) {
        searchTerm = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(searchTerm)}`;
      }
      return (
        url.searchParams.set("q", searchTerm),
        url.searchParams.set("resources[limit]", this.dataset.resultsLimit || 3),
        url.searchParams.set("resources[limit_scope]", "each"),
        url.searchParams.set("resources[options][fields]", "title,body,product_type,variants.title,variants.sku,variants.barcode,vendor"),
        url.searchParams.set("section_id", FoxTheme.utils.getSectionId(this)),
        url
      );
    }

    setupBoostAIURL() {
      // Get Shopify store domain
      let shopDomain = window.Shopify.shop;
      if (shopDomain) {
        shopDomain = shopDomain.replace(/^https?:\/\//, "").replace(/\/$/, "");
      } else {
        return;
      }

      const url = new URL("https://services.mybcapps.com/bc-sf-filter/search");
      url.searchParams.set("shop", shopDomain);
      url.searchParams.set("q", this.getQuery());

      // Add product type filter if selected
      if (this.searchProductTypes && !!this.searchProductTypes.value) {
        url.searchParams.set("product_type", this.searchProductTypes.value);
      }

      url.searchParams.set("limit", "8"); // Fetch 8 products for 2 rows of 4
      url.searchParams.set("page", "1");
      url.searchParams.set("event_type", "search");
      url.searchParams.set("product_available", "0");
      url.searchParams.set("build_filter_tree", "0");
      url.searchParams.set("search_fields", "title,vendor,product_type,tags,body_html,variants.title,variants.sku,variants.barcode");

      url.searchParams.set("currency", this.currency);
      url.searchParams.set("country", this.market);
      return url;
    }

    onChange() {
      if (this.getQuery().length === 0) {
        this.clear();
        return;
      }
      const url = this.setupURL().toString();
      this.renderSection(url);
    }

    renderSection(url) {
      this.cachedMap.has(url)
        ? this.renderSectionFromCache(url)
        : this.renderSectionFromFetch(url);
    }

    renderSectionFromCache(url) {
      const responseText = this.cachedMap.get(url);
      (this.renderSearchResults(responseText), this.setAttribute("results", ""));
    }

    async renderSectionFromFetch(url) {
      this.setLoadingState(true);

      try {
        // First, fetch from Boost AI to get product handles and collections
        const boostAIUrl = this.setupBoostAIURL();
        const shopDomain = boostAIUrl.searchParams.get("shop");

        const boostResponse = await fetch(boostAIUrl);

        if (!boostResponse.ok) {
          // Fallback to original Shopify search
          this.setLoadingState(false);
          return this.renderSectionFromFetchOriginal(url);
        }

        const boostData = await boostResponse.json();

        // Extract product handles from Boost AI results
        const productHandles = (boostData.products || [])
          .map((p) => p.handle)
          .filter(Boolean);

        // Get collections from Boost AI collections endpoint
        let boostCollections = [];
        try {
          const collectionsUrl = new URL(
            "https://services.mybcapps.com/bc-sf-filter/search/collections",
          );
          collectionsUrl.searchParams.set("shop", shopDomain);
          collectionsUrl.searchParams.set("q", this.getQuery());

          // Add product type filter if selected
          if (this.searchProductTypes && !!this.searchProductTypes.value) {
            collectionsUrl.searchParams.set(
              "product_type",
              this.searchProductTypes.value,
            );
          }
          collectionsUrl.searchParams.set("limit", "8");

          const collectionsResponse = await fetch(collectionsUrl);

          if (collectionsResponse.ok) {
            const collectionsData = await collectionsResponse.json();
            if (
              collectionsData.collections &&
              Array.isArray(collectionsData.collections)
            ) {
              boostCollections = collectionsData.collections;
            }
          }
        } catch (error) {
          // Silently fail - will fall back to Shopify collections
        }

        if (productHandles.length === 0) {
          // No products found, render empty results
          this.renderSearchResults("");
          this.setAttribute("results", "");
          this.cachedMap.set(url, "");
          this.setLoadingState(false);
          return;
        }

        // Extract color scheme once before processing all products
        const sectionId = FoxTheme.utils.getSectionId(this);
        let pcardColorScheme = "scheme-1";

        // Find existing cards in predictive search results
        const predictiveSearchContainer = document.querySelector(
          "#PredictiveSearchResults-" + sectionId,
        );
        let existingCard = null;

        if (predictiveSearchContainer) {
          existingCard = predictiveSearchContainer.querySelector(
            ".product-card.product-card-style-card",
          );
        }

        // If not found in predictive search, look anywhere on the page
        if (!existingCard) {
          existingCard = document.querySelector(
            ".product-card.product-card-style-card",
          );
        }

        // Also check if there are multiple cards and find the most common color scheme
        if (!existingCard) {
          const allCards = document.querySelectorAll(
            ".product-card.product-card-style-card",
          );
          if (allCards.length > 0) {
            // Count color schemes
            const colorSchemeCounts = {};
            allCards.forEach((card) => {
              const colorClass = Array.from(card.classList).find(
                (c) =>
                  c.startsWith("color-") &&
                  !c.includes("inherit") &&
                  c !== "color-inherit",
              );
              if (colorClass) {
                const scheme = colorClass.replace("color-", "");
                colorSchemeCounts[scheme] = (colorSchemeCounts[scheme] || 0) + 1;
              }
            });
            // Use the most common color scheme
            const mostCommon = Object.entries(colorSchemeCounts).sort(
              (a, b) => b[1] - a[1],
            )[0];
            if (mostCommon) {
              pcardColorScheme = mostCommon[0];
            }
          }
        } else {
          // Get pcard_color_scheme from the card's color class
          const colorClass = Array.from(existingCard.classList).find(
            (c) =>
              c.startsWith("color-") &&
              !c.includes("inherit") &&
              c !== "color-inherit",
          );
          if (colorClass) {
            // Extract just the scheme part (e.g., "scheme-2" from "color-scheme-2")
            pcardColorScheme = colorClass.replace("color-", "");
          }
        }

        // Fallback: fetch Shopify predictive search to get color scheme
        if (pcardColorScheme === "scheme-1") {
          try {
            const predictiveSearchUrl = this.setupURL();
            const response = await fetch(predictiveSearchUrl);
            if (response.ok) {
              const html = await response.text();
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, "text/html");
              const shopifyCard = doc.querySelector(
                ".product-card.product-card-style-card",
              );
              if (shopifyCard) {
                const shopifyColorClass = Array.from(shopifyCard.classList).find(
                  (c) =>
                    c.startsWith("color-") &&
                    !c.includes("inherit") &&
                    c !== "color-inherit",
                );
                if (shopifyColorClass) {
                  pcardColorScheme = shopifyColorClass.replace("color-", "");
                }
              }
            }
          } catch (error) {
            // Silently fail - will use default scheme
          }
        }

        const showSaleBadge =
          this.dataset.pcardShowSaleBadge !== "false" &&
          this.dataset.pcardShowSaleBadge !== "";
        const saleBadgeType = this.dataset.pcardSaleBadgeType || "show_text";
        const showSecondImage = this.dataset.pcardShowSecondImg !== "false";
        const saleBadgeCrazyWeeks =
          this.dataset.pcardSaleBadgeCrazyWeeks === "true";
        const saleBadgeSettings = {
          showSaleBadge,
          saleBadgeType,
          saleBadgeCrazyWeeks,
        };

        // Fetch each product page and extract the card-product snippet
        const productCardPromises = productHandles.map(async (handle, index) => {
          try {
            // Use Boost AI product data, fetch price from Shopify
            const product = boostData.products[index];
            if (!product) {
              return null;
            }

            // Fetch the product page to get the price snippet
            const productUrl = `${window.shopUrl}/products/${handle}`;
            const response = await fetch(productUrl);

            if (!response.ok) {
              return null;
            }

            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");

            // Extract the canonical PDP price block first:
            // product-information-blocks.liquid => #price-{{ section.id }}[role="status"]
            const priceElement =
              doc.querySelector(
                '.section-main-product [id^="price-"][role="status"] .f-price',
              ) ||
              doc.querySelector(".product-card .f-price") ||
              doc.querySelector(".section-main-product .f-price") ||
              doc.querySelector(".product__info-wrapper .f-price") ||
              doc.querySelector(".f-price");
            const priceHtml = priceElement
              ? priceElement.outerHTML
              : '<div class="f-price">...</div>';
            const ribbonInfo = SearchUtils.getProductRibbonInfo(doc);

          if (product?.price && product.price > 0) {
            const priceItem = priceElement.querySelector('.f-price-item')
            if (priceItem) {
              priceItem.innerHTML = product.price
            }
          }
          if (product?.compare_at_price && product.compare_at_price > 0) {
            const compareToItem = priceElement.querySelector('.f-price-item--strikethrough');
            if (compareToItem) {
              compareToItem.innerHTML = product.compare_at_price;
            }
          }

            // Get product images - images_info is an array of objects
            let firstImage = "";
            let secondImage = "";

            if (product.images_info && product.images_info.length > 0) {
              const firstImageObject = product.images_info[0];
              firstImage = firstImageObject.src || "";
            }

            if (
              showSecondImage &&
              product.images_info &&
              product.images_info.length > 1
            ) {
              const secondImageObject = product.images_info[1];
              secondImage = secondImageObject.src;
            }

            // Fallback: Extract image from product page if not found in Boost AI data
            if (!firstImage) {
              const productImage = doc.querySelector(
                '.product__media img, .product-card__image img, img[src*="/products/"]',
              );
              if (productImage) {
                firstImage = productImage.getAttribute("src") || "";
              }
            }

            // Use color scheme extracted once to ensure all cards match
            // Card renders directly without f-column wrapper, grid handles layout
            const pcardStyle = "card";
            let cardHtml =
              '<div class="product-card product-card-style-' +
              pcardStyle +
              " color-" +
              pcardColorScheme +
              ' color-inherit" data-product-handle="' +
              SearchUtils.escapeHtml(handle) +
              '">';
            cardHtml +=
              '<div class="product-card__wrapper h-full flex flex-col">';

            // Image wrapper - should have the same color scheme as the product-card
            cardHtml +=
              '<div class="product-card__image-wrapper' +
              (secondImage ? "" : " product-card__image-wrapper--main-only") +
              " color-" +
              pcardColorScheme +
              ' bg-none">';
            cardHtml += '<div class="product-card__wishlist-badge">';
            cardHtml +=
              '<div class="wishlist-collection-button"><button type="button" aria-label="Add to Wishlist" class="wkh-button wkh-align-center wkh-align-content-center" data-product-handle="' +
              SearchUtils.escapeHtml(handle) +
              '"><div class="wkh-icon" icon="wishlist"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="21" viewBox="0 0 24 21" class="heart-icon" aria-hidden="true" style="--heart-fill: url(#heartGradient);"><defs><linearGradient id="heartGradient" x1="3.8" y1="0" x2="21.6" y2="1.7" gradientUnits="userSpaceOnUse"><stop stop-color="#DD0044"/><stop offset="1" stop-color="#FF5533"/></linearGradient></defs><path class="heart-path" d="M21.7315 2.05512C20.5096 0.729882 18.833 0 17.0102 0C15.6477 0 14.3999 0.430762 13.3014 1.28022C12.7471 1.709 12.2448 2.23359 11.802 2.84587C11.3594 2.23377 10.8569 1.709 10.3024 1.28022C9.20411 0.430762 7.95631 0 6.59379 0C4.77098 0 3.09421 0.729882 1.87234 2.05512C0.665052 3.36488 0 5.1542 0 7.0937C0 9.08994 0.743929 10.9173 2.3411 12.8445C3.76989 14.5685 5.82339 16.3185 8.2014 18.345C9.0134 19.0371 9.93381 19.8215 10.8895 20.6571C11.142 20.8783 11.466 21 11.802 21C12.1379 21 12.462 20.8783 12.7141 20.6575C13.6698 19.8217 14.5908 19.0369 15.4032 18.3445C17.7808 16.3183 19.8343 14.5685 21.2631 12.8443C22.8603 10.9173 23.604 9.08994 23.604 7.09352C23.604 5.1542 22.939 3.36488 21.7315 2.05512Z" stroke="url(#heartGradient)" stroke-width="1"/></svg></div></button></div>';
            cardHtml += "</div>";
            cardHtml +=
              '<a href="' +
              productUrl +
              '" aria-label="' +
              SearchUtils.escapeHtml(product.title) +
              '" tabindex="-1">';
            cardHtml +=
              '<motion-element data-motion="zoom-out-sm" class="block">';

            if (firstImage) {
              cardHtml +=
                '<div class="media-wrapper product-card__image product-card__image--main" style="--aspect-ratio: 1">';
              cardHtml +=
                '<img src="' +
                firstImage +
                '" alt="' +
                SearchUtils.escapeHtml(product.title) +
                '" loading="lazy" class="motion-reduce" is="image-lazy">';
              cardHtml += "</div>";

              if (secondImage) {
                cardHtml +=
                  '<div class="media-wrapper product-card__image product-card__image--second inset-full hidden md:block" style="--aspect-ratio: 1">';
                cardHtml +=
                  '<img src="' +
                  secondImage +
                  '" alt="' +
                  SearchUtils.escapeHtml(product.title) +
                  '" loading="lazy" class="motion-reduce" is="image-lazy">';
                cardHtml += "</div>";
              }
            } else {
              cardHtml +=
                '<div class="product-card__image product-card__image--main" style="--aspect-ratio: 1;">';
              cardHtml +=
                '<svg class="placeholder-svg" viewBox="0 0 525.5 525.5"><rect width="525.5" height="525.5" fill="#f0f0f0"/></svg>';
              cardHtml += "</div>";
            }

            cardHtml += "</motion-element></a>";
            const ribbonBadgeHtml = SearchUtils.getRibbonBadgeHtml(ribbonInfo);
            if (ribbonBadgeHtml) {
              cardHtml += ribbonBadgeHtml;
            }
            cardHtml += "</div>";

            const saleBadgeHtml = SearchUtils.getSaleBadgeHtml(
              product,
              saleBadgeSettings,
            );
            if (saleBadgeHtml) {
              cardHtml += saleBadgeHtml;
            }

            // Info section
            cardHtml += '<div class="product-card__info text-left">';
            cardHtml +=
              '<div class="jdgm-widget jdgm-preview-badge" data-id="' +
              product.id +
              '" data-auto-install="false"></div>';
            const showVendor = this.dataset.pcardShowVendor !== "false";
            if (showVendor && product.vendor) {
              const brandLink = SearchUtils.getBrandLinkFromDocument(doc, product.vendor);
              cardHtml +=
                '<a href="' +
                SearchUtils.escapeHtml(brandLink.url) +
                '" class="text-body reversed-link" aria-label="' +
                SearchUtils.escapeHtml(
                  (typeof FoxTheme !== "undefined" &&
                    FoxTheme.accessibility?.vendor) ||
                  "Vendor ",
                ) +
                SearchUtils.escapeHtml(product.vendor) +
                '"><span class="reversed-link__text">' +
                SearchUtils.escapeHtml(product.vendor) +
                "</span></a>";
            }
            // Title - with 2-line clamp
            cardHtml += '<p class="product-card__title text-pcard-title">';
            cardHtml +=
              '<a class="reversed-link block text-limit-2-lines" href="' +
              productUrl +
              '">';
            cardHtml +=
              '<span class="reversed-link__text">' +
              SearchUtils.escapeHtml(product.title) +
              "</span>";
            cardHtml += "</a></p>";

            // Price (from fetched HTML) - this will be above the button
            cardHtml += priceHtml;

            // Create product form with variant ID from Boost AI data
            let variantId = null;
            let isAvailable = false;

            if (product.variants && product.variants.length > 0) {
              // Find first available variant, or use first variant if none available
              const availableVariant =
                product.variants.find((v) => v.available === true) ||
                product.variants[0];
              variantId = availableVariant.id;
              isAvailable = availableVariant.available === true;
            }

            cardHtml +=
              '<div class="product-card__grid-actions product-card__grid-only">';

            if (variantId) {
              // Create a proper product form for add to cart (matching the original structure)
              cardHtml +=
                '<div class="product-form" data-hide-errors="false" data-section-id="' +
                sectionId +
                '">';
              cardHtml +=
                '<form method="post" action="/cart/add" accept-charset="UTF-8" class="main-product-form grid gap-4" enctype="multipart/form-data" data-type="add-to-cart-form" is="product-form" data-hide-errors="false">';
              cardHtml +=
                '<input type="hidden" name="form_type" value="product">';
              cardHtml += '<input type="hidden" name="utf8" value="✓">';
              cardHtml +=
                '<input type="hidden" name="id" value="' +
                variantId +
                '" class="product-variant-id">';
              cardHtml +=
                '<div class="product-form__buttons flex flex-wrap items-end gap-x-2 gap-y-4">';
              cardHtml +=
                '<button type="submit" name="add" class="product-form__submit btn btn--primary"' +
                (isAvailable ? "" : " disabled") +
                ">";
              cardHtml += '<span class="btn__icon-cart">';
              cardHtml += isAvailable
                ? '<svg viewBox="0 0 20 20" fill="none" class="icon icon-cart icon--medium icon--thick" xmlns="http://www.w3.org/2000/svg"><path d="M6.875 18.125C7.56536 18.125 8.125 17.5654 8.125 16.875C8.125 16.1846 7.56536 15.625 6.875 15.625C6.18464 15.625 5.625 16.1846 5.625 16.875C5.625 17.5654 6.18464 18.125 6.875 18.125Z" fill="currentColor"/><path d="M15 18.125C15.6904 18.125 16.25 17.5654 16.25 16.875C16.25 16.1846 15.6904 15.625 15 15.625C14.3096 15.625 13.75 16.1846 13.75 16.875C13.75 17.5654 14.3096 18.125 15 18.125Z" fill="currentColor"/><path d="M1.25 2.5H3.125L5.99609 12.8344C6.06916 13.0976 6.22643 13.3296 6.44384 13.4949C6.66126 13.6603 6.92685 13.7499 7.2 13.75H14.9219C15.1952 13.7501 15.4609 13.6605 15.6785 13.4952C15.8961 13.3298 16.0535 13.0977 16.1266 12.8344L18.125 5.625H3.99297" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
                : '<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-bell icon--medium icon--thick" viewBox="0 0 24 24" fill="none"><path d="M6.44784 8.96942C6.76219 6.14032 9.15349 4 12 4C14.8465 4 17.2378 6.14032 17.5522 8.96942L17.804 11.2356C17.8072 11.2645 17.8088 11.279 17.8104 11.2933C17.9394 12.4169 18.3051 13.5005 18.8836 14.4725C18.8909 14.4849 18.8984 14.4973 18.9133 14.5222L19.4914 15.4856C20.0159 16.3599 20.2782 16.797 20.2216 17.1559C20.1839 17.3946 20.061 17.6117 19.8757 17.7668C19.5971 18 19.0873 18 18.0678 18H5.93223C4.91268 18 4.40291 18 4.12434 17.7668C3.93897 17.6117 3.81609 17.3946 3.77841 17.1559C3.72179 16.797 3.98407 16.3599 4.50862 15.4856L5.08665 14.5222C5.10161 14.4973 5.10909 14.4849 5.11644 14.4725C5.69488 13.5005 6.06064 12.4169 6.18959 11.2933C6.19123 11.279 6.19283 11.2645 6.19604 11.2356L6.44784 8.96942Z" stroke="currentColor"/><path d="M9.10222 18.4059C9.27315 19.1501 9.64978 19.8077 10.1737 20.2767C10.6976 20.7458 11.3396 21 12 21C12.6604 21 13.3024 20.7458 13.8263 20.2767C14.3502 19.8077 14.7269 19.1501 14.8978 18.4059" stroke="currentColor" stroke-linecap="round"/></svg>';
              cardHtml += "</span>";
              cardHtml +=
                '<span class="btn__text-card">' +
                (isAvailable
                  ? FoxTheme.variantStrings?.addToCart || "In den Warenkorb"
                  : FoxTheme.variantStrings?.soldOut || "Ausverkauft") +
                "</span>";
              cardHtml += '<div class="loading__spinner hidden">';
              cardHtml +=
                '<svg aria-hidden="true" focusable="false" class="spinner icon icon--extra-medium" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">';
              cardHtml +=
                '<circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle>';
              cardHtml += "</svg>";
              cardHtml += "</div>";
              cardHtml += "</button>";
              cardHtml += "</div>";
              cardHtml +=
                '<input type="hidden" name="product-id" value="' +
                product.id +
                '">';
              cardHtml +=
                '<input type="hidden" name="section-id" value="' +
                sectionId +
                '">';
              cardHtml += "</form>";
              cardHtml += "</div>";
            } else {
              // Fallback to link if variant not found
              cardHtml +=
                '<a href="' +
                productUrl +
                '" class="btn btn--primary btn--small">';
              cardHtml +=
                '<span class="btn__text">' +
                (FoxTheme.variantStrings?.addToCart || "In den Warenkorb") +
                "</span>";
              cardHtml += "</a>";
            }

            cardHtml += "</div>";

            cardHtml += "</div></div></div>";

            // Note: Card renders directly without f-column wrapper, grid handles layout
            return cardHtml;
          } catch (error) {
            return null;
          }
        });

        const productCards = await Promise.all(productCardPromises);
        const validCards = productCards.filter((card) => card !== null);

        if (validCards.length === 0) {
          this.setLoadingState(false);
          return this.renderSectionFromFetchOriginal(url);
        }

        // Combine all cards into HTML
        const cardsHtml = validCards.join("");

        // Render the results (products and collections)
        this.renderBoostSearchResults(cardsHtml, boostCollections);
        this.setAttribute("results", "");
        this.cachedMap.set(url, cardsHtml);
        this.setLoadingState(false);
      } catch (error) {
        // Fallback to original Shopify search
        this.setLoadingState(false);
        return this.renderSectionFromFetchOriginal(url);
      }
    }

    renderSectionFromFetchOriginal(url) {
      this.setLoadingState(true);

      fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error("Network response was not ok");
          return response.text();
        })
        .then((responseText) => {
          this.renderSearchResults(responseText);
          this.cachedMap.set(url, responseText);
        })
        .catch((error) => {
          this.setAttribute("error", "Failed to load data");
        })
        .finally(() => this.setLoadingState(false));
    }

    async fetchShopifyCollections(targetElement) {
      if (!targetElement) {
        return;
      }

      try {
        const predictiveSearchUrl = this.setupURL();
        const predictiveResponse = await fetch(predictiveSearchUrl);

        if (predictiveResponse.ok) {
          const predictiveHtml = await predictiveResponse.text();
          const parser = new DOMParser();
          const predictiveDoc = parser.parseFromString(
            predictiveHtml,
            "text/html",
          );
          const shopifyCollectionsList = predictiveDoc.querySelector(
            "#predictive-search-results-collection-list",
          );

          if (shopifyCollectionsList) {
            // Try multiple ways to find the collections list
            let collectionsList = targetElement.querySelector(
              "#predictive-search-results-collection-list",
            );

            if (!collectionsList) {
              collectionsList = document.querySelector(
                "#predictive-search-results-collection-list",
              );
            }

            if (!collectionsList) {
              const collectionsSection =
                targetElement.querySelector(
                  ".predictive-search-result--collections",
                ) ||
                document.querySelector(".predictive-search-result--collections");
              if (collectionsSection) {
                collectionsList =
                  collectionsSection.querySelector(
                    "#predictive-search-results-collection-list",
                  ) ||
                  collectionsSection.querySelector(
                    "ul.predictive-search-result__list",
                  );
              }
            }

            if (collectionsList) {
              collectionsList.innerHTML = shopifyCollectionsList.innerHTML;
            }
          }
        }
      } catch (error) {
        // Silently fail
      }
    }

    renderBoostSearchResults(cardsHtml, collections = []) {
      const sectionId = FoxTheme.utils.getSectionId(this);
      const id = "PredictiveSearchResults-" + sectionId;
      const targetElement = document.getElementById(id);

      if (targetElement) {
        // Ensure structure: .flex > .search__box-products > .swipe-mobile > #predictive-search-results-products-list
        let flexContainer =
          targetElement.querySelector(
            ".flex.w-full.flex-col.gap-y-6.md\\:flex-row",
          ) ||
          Array.from(targetElement.children).find(
            (el) =>
              el.classList.contains("flex") && el.classList.contains("w-full"),
          );

        if (!flexContainer) {
          flexContainer = document.createElement("div");
          flexContainer.className = "flex w-full flex-col gap-y-6 md:flex-row";
          targetElement.appendChild(flexContainer);
        }

        // Find or create search__box-products
        let searchBoxProducts = flexContainer.querySelector(
          ".search__box-products",
        );
        if (!searchBoxProducts) {
          searchBoxProducts = document.createElement("div");
          searchBoxProducts.className =
            "flex flex-col flex-grow search__box-products order-last md:order-first";
          flexContainer.insertBefore(searchBoxProducts, flexContainer.firstChild);
        }

        // Find or create the products result container
        let productsResult = searchBoxProducts.querySelector(
          ".predictive-search-result--products",
        );
        if (!productsResult) {
          productsResult = document.createElement("div");
          productsResult.className =
            "flex flex-col gap-5 lg:gap-6 predictive-search-result search__box-item predictive-search-result--products";

          // Add the heading
          const heading = document.createElement("h4");
          heading.className = "h4 predictive-search-result__heading";
          heading.textContent = FoxTheme.searchStrings?.relatedProducts; // Verwandte Produkte
          productsResult.appendChild(heading);

          searchBoxProducts.appendChild(productsResult);
        } else {
          // Ensure heading exists even if container already exists
          let heading = productsResult.querySelector(
            ".predictive-search-result__heading",
          );
          if (!heading) {
            heading = document.createElement("h4");
            heading.className = "h4 predictive-search-result__heading";
            heading.textContent = FoxTheme.searchStrings?.relatedProducts; // Verwandte Produkte
            productsResult.insertBefore(heading, productsResult.firstChild);
          }
        }

        // Find or create swipe-mobile wrapper
        let swipeMobile = productsResult.querySelector(".swipe-mobile");
        if (!swipeMobile) {
          swipeMobile = document.createElement("div");
          swipeMobile.className = "swipe-mobile swipe-mobile--2-cols";
          productsResult.appendChild(swipeMobile);
        }

        // Find or create the products list container
        let productsList = swipeMobile.querySelector(
          "#predictive-search-results-products-list",
        );
        if (!productsList) {
          productsList = document.createElement("div");
          productsList.id = "predictive-search-results-products-list";
          productsList.className =
            "f-grid f-grid--gap-2xs f-grid--row-gap-inherit swipe-mobile__inner";
          productsList.setAttribute("data-layout", "grid"); // Required for product-card__wrapper to be flex
          swipeMobile.appendChild(productsList);
        } else {
          // Ensure data-layout is set and classes are correct even if container already exists
          if (!productsList.hasAttribute("data-layout")) {
            productsList.setAttribute("data-layout", "grid");
          }
          // Update classes to match original
          productsList.className =
            "f-grid f-grid--gap-2xs f-grid--row-gap-inherit swipe-mobile__inner";
        }

        // Set 4 columns for desktop (2 rows of 4), mobile handled by swipe-mobile
        productsList.style.setProperty("--f-columns-xl", "4");
        productsList.style.setProperty("--f-columns-lg", "4");
        productsList.style.setProperty("--f-columns-md", "2");
        productsList.style.setProperty("--f-columns-mobile", "2");

        // Insert the product cards HTML directly (no wrapper - cards are direct children of grid)
        productsList.innerHTML = cardsHtml;

        // Load Judge.me star rating badges from cache server
        if (window.JudgeMeForSearch?.loadBadges) {
          JudgeMeForSearch.loadBadges(productsList).catch(() => { });
        }

        // Add or update the "View all results" button
        let viewAllButton = productsResult.querySelector(".search__results-all");
        let button = viewAllButton?.querySelector("button");
        let buttonText = button?.querySelector(".btn__text");

        // Get the search query for the button text
        const searchQuery = this.getQuery() || "";
        // Decode and clean query
        const decodedQuery = decodeURIComponent(searchQuery).replace(/%20/g, " ");
        const buttonLabel = (FoxTheme.searchStrings?.viewAllResults).replace(
          "[terms]",
          decodedQuery,
        ); // Alle Ergebnisse für „[terms]" anzeigen

        if (!viewAllButton) {
          viewAllButton = document.createElement("div");
          viewAllButton.className = "search__results-all block";

          button = document.createElement("button");
          button.type = "submit";
          button.className = "btn btn--primary";
          button.setAttribute("form", "PredictiveSearch-" + sectionId);

          buttonText = document.createElement("span");
          buttonText.className = "btn__text flex items-center gap-2";

          // Create text node for the label
          const labelText = document.createTextNode(buttonLabel);
          buttonText.appendChild(labelText);

          // Add arrow icon SVG
          const arrowSvg = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "svg",
          );
          arrowSvg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
          arrowSvg.setAttribute(
            "class",
            "icon icon-arrow-right rtl-flip-x icon--extra-medium",
          );
          arrowSvg.setAttribute("viewBox", "0 0 18 18");
          arrowSvg.setAttribute("fill", "none");

          const path1 = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "path",
          );
          path1.setAttribute("d", "M2.8125 9H15.1875");
          path1.setAttribute("stroke", "currentColor");
          path1.setAttribute("stroke-width", "1.5");
          path1.setAttribute("stroke-linecap", "round");
          path1.setAttribute("stroke-linejoin", "round");

          const path2 = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "path",
          );
          path2.setAttribute("d", "M10.125 3.9375L15.1875 9L10.125 14.0625");
          path2.setAttribute("stroke", "currentColor");
          path2.setAttribute("stroke-width", "1.5");
          path2.setAttribute("stroke-linecap", "round");
          path2.setAttribute("stroke-linejoin", "round");

          arrowSvg.appendChild(path1);
          arrowSvg.appendChild(path2);
          buttonText.appendChild(arrowSvg);

          button.appendChild(buttonText);
          viewAllButton.appendChild(button);
          productsResult.appendChild(viewAllButton);
        } else {
          // Update existing button label (keep arrow icon)
          const existingTextNode = Array.from(buttonText.childNodes).find(
            (node) => node.nodeType === Node.TEXT_NODE,
          );
          if (existingTextNode) {
            existingTextNode.textContent = buttonLabel;
          } else {
            // If no text node exists, insert one before the arrow icon
            const arrowSvg = buttonText.querySelector("svg");
            const labelText = document.createTextNode(buttonLabel);
            if (arrowSvg) {
              buttonText.insertBefore(labelText, arrowSvg);
            } else {
              buttonText.appendChild(labelText);
            }
          }
        }

        // Render collections if available
        if (collections.length > 0) {
          // Find or create collections section in .search__box-others
          let collectionsList = targetElement.querySelector(
            "#predictive-search-results-collection-list",
          );

          if (!collectionsList) {
            // Look for the search__box-others container
            const searchBoxOthers = targetElement.querySelector(
              ".search__box-others",
            );

            if (searchBoxOthers) {
              // Look for existing collections section
              let collectionsSection = searchBoxOthers.querySelector(
                ".predictive-search-result--collections",
              );

              if (!collectionsSection) {
                // Create the collections section
                collectionsSection = document.createElement("div");
                collectionsSection.className =
                  "grid gap-3 predictive-search-result search__box-item predictive-search-result--collections";

                const heading = document.createElement("h4");
                heading.className = "h6 predictive-search-result__heading";
                heading.textContent = FoxTheme.searchStrings?.collections; // Sammlungen

                const list = document.createElement("ul");
                list.id = "predictive-search-results-collection-list";
                list.setAttribute("role", "group");
                list.setAttribute(
                  "aria-labelledby",
                  "predictive-search-collection",
                );
                list.className =
                  "predictive-search-result__list flex flex-col gap-y-2 text-subtext";

                collectionsSection.appendChild(heading);
                collectionsSection.appendChild(list);
                searchBoxOthers.appendChild(collectionsSection);
              }

              collectionsList =
                collectionsSection.querySelector(
                  "#predictive-search-results-collection-list",
                ) ||
                collectionsSection.querySelector(
                  "ul.predictive-search-result__list",
                );
            } else {
              // search__box-others doesn't exist, need to create the whole structure
              const flexContainer =
                targetElement.querySelector(
                  ".flex.w-full.flex-col.gap-y-6.md\\:flex-row",
                ) || targetElement.querySelector(".flex.w-full");

              if (flexContainer) {
                const searchBoxOthers = document.createElement("div");
                searchBoxOthers.className =
                  "search__box-others flex flex-col gap-6 lg:gap-8";

                const collectionsSection = document.createElement("div");
                collectionsSection.className =
                  "grid gap-3 predictive-search-result search__box-item predictive-search-result--collections";

                const heading = document.createElement("h4");
                heading.className = "h6 predictive-search-result__heading";
                heading.textContent = FoxTheme.searchStrings?.collections; // Sammlungen

                const list = document.createElement("ul");
                list.id = "predictive-search-results-collection-list";
                list.setAttribute("role", "group");
                list.setAttribute(
                  "aria-labelledby",
                  "predictive-search-collection",
                );
                list.className =
                  "predictive-search-result__list flex flex-col gap-y-2 text-subtext";

                collectionsSection.appendChild(heading);
                collectionsSection.appendChild(list);
                searchBoxOthers.appendChild(collectionsSection);
                flexContainer.appendChild(searchBoxOthers);

                collectionsList = list;
              }
            }
          }

          if (collectionsList) {
            const collectionsHtml = collections
              .map((collection, index) => {
                // Boost AI collections structure: {handle, id, title, url, etc.}
                const collectionUrl = collection.handle
                  ? `/collections/${collection.handle}`
                  : "";
                const collectionTitle = collection.title || "";
                return `<li id="predictive-search-option-collection-${index + 1}" class="predictive-search__list-item flex" role="option" aria-selected="false">
              <a href="${collectionUrl}" class="reversed-link">${SearchUtils.escapeHtml(collectionTitle)}</a>
            </li>`;
              })
              .join("");
            collectionsList.innerHTML = collectionsHtml;
          }
        } else {
          // Fallback: fetch collections from Shopify predictive search (async)
          this.fetchShopifyCollections(targetElement);
        }

        this.searchContent?.classList.remove("hidden");

        if (
          this.isHeaderSearch &&
          !document.body.classList.contains("search-open")
        ) {
          document.body.classList.add("search-open");
          this.toggleNavButtonVisibility(true);
          this.classList.add("predictive-search-open");
        } else {
          this.classList.add("predictive-search-open");
        }
      }
    }
    renderSearchResults(responseText) {
      const id = "PredictiveSearchResults-" + FoxTheme.utils.getSectionId(this);
      const targetElement = document.getElementById(id);

      if (targetElement) {
        const parser = new DOMParser();
        const parsedDoc = parser.parseFromString(responseText, "text/html");
        const contentElement = parsedDoc.getElementById(id);

        if (contentElement) {
          this.searchContent?.classList.remove("hidden");
          targetElement.innerHTML = contentElement.innerHTML;

          if (
            this.isHeaderSearch &&
            !document.body.classList.contains("search-open")
          ) {
            document.body.classList.add("search-open");
            this.toggleNavButtonVisibility(true);
            this.classList.add("predictive-search-open");
          } else {
            this.classList.add("predictive-search-open");
          }
        }
      }
    }

    handleClickOutside(event) {
      const target = event.target;
      const shouldClose = this.isHeaderSearch
        ? !this.contains(target) &&
        ((target.classList.contains("fixed-overlay") &&
          target.closest(".header-section")) ||
          target.classList.contains("header__search-close"))
        : !this.contains(target);

      if (shouldClose) {
        setTimeout(() => this.toggleSearchState(false));
      }
    }

    toggleSearchState(isOpen) {
      this.classList.toggle(this.states.OPEN, isOpen);
      if (this.isHeaderSearch) {
        document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
        this.toggleNavButtonVisibility(!isOpen);
      }
      if (!isOpen && this.searchRecommendationEmpty) {
        this.searchContent?.classList.add("hidden");
      }
    }

    toggleNavButtonVisibility(isOpen) {
      this.toggleNavigationButton &&
        this.toggleNavigationButton.classList.toggle("is-show", isOpen);
    }

    setLoadingState(isLoading) {
      if (isLoading) {
        this.setAttribute("loading", "true");
        this.resetButton?.classList.add(this.states.LOADING);
      } else {
        this.removeAttribute("loading");
        this.resetButton?.classList.remove(this.states.LOADING);
        this.setAttribute("results", "true");
      }
    }
  }
  if (!customElements.get("predictive-search")) {
    customElements.define("predictive-search", PredictiveSearch);
  }
}
